import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import './Style/AnswerSheet.css';
import Loading from "../../Common/Loading";
import nav_previous_n from '../Tutorials/Image/nav_previous_n.png';
import nav_next_n from '../Tutorials/Image/nav_next_n.png';
import next_test from '../Tutorials/Image/next_test.png';
import Close from '../Tutorials/Image/x_close_btn_o.png';
import got_it_btn_n from '../Tutorials/Image/got_it_btn_n.png';
import { markQuestion } from "../../services/AnswerSheetLogic";

const ASSETS_BASE_URL = process.env.REACT_APP_ASSETS_BASE_URL || "/";

const CorrectAnswerIcon = `${ASSETS_BASE_URL}Image/AnswerSheet/CorrectAnswer.png`;
const WrongAnswerIcon = `${ASSETS_BASE_URL}Image/AnswerSheet/WrongAnswer.png`;
const SkippedAnswerIcon = `${ASSETS_BASE_URL}Image/AnswerSheet/SkippedAnswer.png`;


interface QuestionData {
  questionIndex: number;
  lessonName: string;
  questionHtml: string;
  solutionHtml: string;
  hasSolution: boolean;
  questionType: string;
  shuffledOptionsOrder: string;
  noOfOptions: number;
  enteredCorrectAnswer: string;
  enteredWrongAnswer: string;
  enteredBlankAnswer: string;
  subNo: number;
  varValues: string;
  marks: number;
  status: number;
  folder: string;
  questionNo: string;
}

function sanitizeSolutionHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/<body[^>]*>/i, "<body>");
}

function addAnswerSheetOptionPadding(doc: Document): void {
  const optionRows = doc.querySelectorAll("tr[id^='KSD_TR_']");
  if (optionRows.length === 0) return;

  optionRows.forEach((row) => {
    row.classList.add("answersheet-option-row");
    row.closest("table")?.classList.add("answersheet-option-table");
  });

  if (doc.getElementById("answersheet-option-padding")) return;

  const style = doc.createElement("style");
  style.id = "answersheet-option-padding";
  style.textContent = `
    /* Applied only within the AnswerSheet question iframe. */
    .answersheet-option-table {
      border-collapse: separate !important;
      border-spacing: 0 8px !important;
    }
    .answersheet-option-row > td {
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
  `;
  doc.head.appendChild(style);
}

/**
 * Solution files are legacy table layouts.  Present every step as a readable
 * two-column row: the step/prompt at the left and its result at the right.
 */
function structureSolutionSteps(doc: Document): void {
  const stepRows = Array.from(doc.querySelectorAll<HTMLTableRowElement>("tr[id^='trSolStepLHS']"));

  stepRows.forEach((row) => {
    row.style.display = "table-row";
    row.style.width = "100%";
    row.style.background = "#fff";

    const cells = Array.from(row.children).filter(
      (child): child is HTMLTableCellElement => child.tagName === "TD"
    );
    if (cells.length === 0) return;

    const promptCell = cells[0];
    const stepLabel = promptCell.querySelector<HTMLFontElement>("font");
    if (stepLabel) {
      if (!/^\s*Step\s+/i.test(stepLabel.textContent || "")) {
        stepLabel.insertBefore(doc.createTextNode("Step "), stepLabel.firstChild);
      }
      stepLabel.style.color = "#000";
    }

    // The source uses column two for the explanation and the final column for
    // the answer. Move only the explanation into the prompt column so the
    // final answer remains aligned as the right-hand cell.
    if (cells.length >= 3) {
      const explanationCell = cells[1];
      if (!explanationCell.dataset.answerSheetExpanded) {
        if (explanationCell.innerHTML.trim()) {
          const explanation = doc.createElement("div");
          explanation.className = "answersheet-step-explanation";
          explanation.innerHTML = explanationCell.innerHTML;
          promptCell.appendChild(explanation);
        }
        explanationCell.remove();
      }

      // When an expanded legacy Show button occupies the middle cell, the
      // original trailing answer cell is empty. Remove it to keep the normal
      // prompt-and-answer two-column layout.
      const trailingCell = cells[cells.length - 1];
      const trailingCellText = trailingCell.textContent?.replace(/\u00a0/g, "").trim();
      if (explanationCell.dataset.answerSheetExpanded && !trailingCellText) {
        trailingCell.remove();
      }
    }

    const finalCells = Array.from(row.children).filter(
      (child): child is HTMLTableCellElement => child.tagName === "TD"
    );
    finalCells.forEach((cell, index) => {
      cell.removeAttribute("align");
      cell.removeAttribute("valign");
      cell.style.background = "#fff";
      cell.style.border = "1px solid #d3d3d3";
      cell.style.padding = "0px";
      cell.style.verticalAlign = "middle";
      cell.style.color = "#000";
      cell.style.width = index === 0 ? "50%" : `${50 / Math.max(1, finalCells.length - 1)}%`;

      cell.querySelectorAll<HTMLElement>("*").forEach((element) => {
        if (element.tagName === "FONT" && element.getAttribute("color")) return;
        if (element.tagName !== "SUP" && element.tagName !== "SUB") {
          element.style.fontFamily = element.closest("em, i") ? "serif" : "Arial, sans-serif";
        }
      });
    });

    const label = promptCell.querySelector<HTMLElement>("font");
    if (label) {
      label.style.display = "block";
      label.style.marginBottom = "20px";
      label.style.fontWeight = "bold";
      label.style.fontSize = "24px";
    }
  });
}

const AnswerSheet: React.FC = () => {
  const [searchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const kTestQuestionListRef = useRef<HTMLDivElement>(null);

  const [questions, setQuestions] = useState<QuestionData[]>([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showSolution, setShowSolution] = useState(false);
  const [solutionHtml, setSolutionHtml] = useState<string | null>(null);
  const [solutionLoading, setSolutionLoading] = useState(false);

  const dicCode = searchParams.get("DicCode");
  const lsnTestCode = searchParams.get("LsnTestCode");
  const sessionId = searchParams.get("SessionID");
  const whichQuestion = searchParams.get("WhichQuestion") || "All";
  const url = process.env.REACT_APP_DOCUMENT_LINK ;

  useEffect(() => {
    const fetchAnswerSheetData = async () => {
      setError(null);
      if (!dicCode || !lsnTestCode || !sessionId) {
        setError("Missing required test information in URL.");
        setLoading(false);
        return;
      }

      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        setError("Authentication token not found.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(`${url}get_answer_sheet_data/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            dicCode: dicCode,
            lsnTestCode: lsnTestCode,
            sessionId: sessionId,
            whichQuestion: whichQuestion,
          }),
        });

        if (response.status === 401) throw new Error("Session expired. Please log in again.");
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || "Failed to load answer sheet data.");
        }

        const data = await response.json();

        if (data.questions && data.questions.length > 0) {
          setQuestions(data.questions);
          setTotalQuestions(data.totalQuestions);
          setCurrentQuestionIndex(0);
          setShowSolution(false);
        } else {
          setError("No questions found for this filter.");
          setQuestions([]);
          setTotalQuestions(data.totalQuestions || 0);
        }
      } catch (e: any) {
        setError(e.message);
        setQuestions([]);
        setTotalQuestions(0);
      } finally {
        setLoading(false);
      }
    };

    fetchAnswerSheetData();
  }, [dicCode, lsnTestCode, sessionId, whichQuestion, url]);


  const handleNext = () => {
    if (!loading && currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowSolution(false);
    }
  };

  const handlePrevious = () => {
    if (!loading && currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowSolution(false);
    }
  };

  const toggleSolution = async (show: boolean) => {
  if (loading || !currentQuestion) return;

  if (show && currentQuestion.hasSolution) {
    setShowSolution(true);

    const qnIframe = document.getElementById("iFrameQn") as HTMLIFrameElement;
    if (qnIframe) {
      qnIframe.style.pointerEvents = "none";
    }

    setTimeout(() => {
    const solDiv = document.getElementById("divSolutionIFrameT");
    if (solDiv) {
      solDiv.style.setProperty("display", "flex", "important");
      solDiv.style.setProperty("visibility", "visible", "important");
      solDiv.style.setProperty("position", "fixed", "important");
      // solDiv.style.setProperty("left", "0px", "important");
      // solDiv.style.setProperty("right", "0px", "important");
      // solDiv.style.setProperty("top", "0px", "important");
      // solDiv.style.setProperty("bottom", "0px", "important");
      solDiv.style.setProperty("overflow", "hidden", "important");
    }

    const bg = document.getElementById("divBackground");
    if (bg) {
      bg.style.setProperty("display", "block", "important");
    }
  }, 0);


    // 4️⃣ Start loading solution
    setSolutionLoading(true);

    try {
      const accessToken = localStorage.getItem("access_token");

      const response = await fetch(`${url}get_answer_sheet_solution/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          folder: currentQuestion.folder,
          questionNo: currentQuestion.questionNo,
          country: "Australia",
          varValues: currentQuestion.varValues,
        }),
      });

      const data = await response.json();
      const cleanHtml = sanitizeSolutionHtml(data.solutionHtml);
      // const cleanHtml = ''

      setSolutionHtml(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <style>
  html, body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    background-color: #fff; /* ivory-like background used earlier */
    color: #fff;
  }

  #solutionHeader {
    background: #20699b;
    height: 35px;
    line-height: 35px;
    text-align: center;
    font-weight: bold;
    color: #fff;
    border-bottom: 2px solid #d3d3d3;
  }

  /* Container table that holds steps */
  .sol-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;           /* keep columns consistent */
    margin: 0;
  }

  /* columns: number | question | icon | answer */
  .sol-table col.num { width: 8%; }
  .sol-table col.qn  { width: 62%; }
  .sol-table col.icon{ width: 10%; }
  .sol-table col.ans { width: 20%; }

  tr[id^="trSolStepLHS"] td {
    /* padding: 12px 10px; */
    vertical-align: middle;
  }
  tr[id^="trSolStepLHS"]:nth-child(odd) td { background: #FFF; } /* peach */
  tr[id^="trSolStepLHS"]:nth-child(even) td { background: #FFF; } /* pale yellow */

  td.num-col {
    text-align: center;
    font-size: 28px;
    color: #fff;
  }

  /* question column */
  td.qn-col {
    text-align: center;
    font-size: 20px;
    color: #0033cc;        /* blue question text color */
  }

  /* show/hide icon */
  td.icon-col {
    text-align: center;
  }
  td.icon-col img {
    width: 40px;
    height: 40px;
    cursor: pointer;
  }

  /* answer column: right side */
  td.ans-col {
    text-align: left;
    padding-left: 20px;
    color: #d00;           /* red answer text color */
    font-size: 18px;
  }

  /* ensure images and tables fit */
  img { max-width: 100%; height: auto; display: inline-block; }

  /* make answers scrollable if long */
  .answer-wrapper {
    max-height: calc(100vh - 35px - 40px); /* viewport minus header & padding */
    overflow-y: auto;
  }
</style>
          </head>
          <body style="margin:0;padding:0px;font-family:Arial;">
            ${cleanHtml}
          </body>
        </html>
      `);
    } catch (err) {
      console.error("Error loading solution:", err);
      setSolutionHtml("<p>Error loading solution</p>");
    } finally {
      setSolutionLoading(false);
    }
  } else {
    // ===== CLOSE SOLUTION =====

    setShowSolution(false);
    setSolutionHtml(null);

    // Re-enable question iframe
    const qnIframe = document.getElementById("iFrameQn") as HTMLIFrameElement;
    if (qnIframe) {
      qnIframe.style.pointerEvents = "auto";
    }

    // Hide background overlay
    const bg = document.getElementById("divBackground");
    if (bg) {
      bg.style.setProperty("display", "none", "important");
      bg.style.setProperty("background-color", "rgba(0,0,0,0.29)", "important");
      bg.style.setProperty("z-index", "300", "important");
    }

    // Hide solution container + reset height
    const solDiv = document.getElementById("divSolutionIFrameT");
    if (solDiv) {
      solDiv.style.setProperty("display", "none", "important");
      solDiv.style.removeProperty("height");
    }
  }
};


  const currentQuestion = !loading && questions.length > 0 ? questions[currentQuestionIndex] : null;

  // --- Logic to write HTML content into iframe ---
  useEffect(() => {
    if (!loading && currentQuestion && iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(currentQuestion.questionHtml);
        doc.close();
      }
    }
  }, [currentQuestionIndex, loading, currentQuestion]);

  // --- Logic to trigger marking on iframe load ---
  const handleIframeLoad = () => {
    if (!iframeRef.current || !iframeRef.current.contentDocument || !currentQuestion) return;

    const doc = iframeRef.current.contentDocument;

    // Optional: Inject base styles if needed to match legacy behavior
    const style = doc.createElement('style');
    style.innerHTML = `body { font-family: Arial, sans-serif; overflow-y: hidden; background-color: white; }`;
    doc.head.appendChild(style);

    // Call the marking logic service
    const hideAnswerIndexes = (answer: string): string => {
      if (!answer) return "";

      return answer
        .split("|")
        .map(item => /^\d{2}/.test(item) ? item.substring(2) : item)
        .join("|");
    };

    markQuestion(
      doc,
      currentQuestion.questionType,
      hideAnswerIndexes(currentQuestion.enteredCorrectAnswer),
      hideAnswerIndexes(currentQuestion.enteredWrongAnswer),
      currentQuestion.enteredBlankAnswer,
      ""
    );

    addAnswerSheetOptionPadding(doc);
  };

  const getStatusInfo = () => {
    if (loading || !currentQuestion) return { icon: '', comment: '-' };

    switch (currentQuestion.status) {
      case 1:
        return { icon: CorrectAnswerIcon, comment: "Correct Answer" };
      case 0:
        return { icon: SkippedAnswerIcon, comment: "Question Skipped" };
      case -1:
      default:
        return { icon: WrongAnswerIcon, comment: "Wrong Answer" };
    }
  };

  const statusInfo = getStatusInfo();

  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const hasQuestions = questions.length > 0;
  const isKTestNavigation = questions.some((question) =>
    question.folder?.trim().toUpperCase().startsWith("K")
  ) || (lsnTestCode?.trim().toUpperCase().startsWith("K") ?? false);

  const selectQuestion = (questionIndex: number) => {
    if (!loading && questionIndex !== currentQuestionIndex) {
      setCurrentQuestionIndex(questionIndex);
      setShowSolution(false);
    }
  };

  // Keep the active K-test question number visible and centred while navigating.
  useEffect(() => {
    if (!isKTestNavigation || !hasQuestions || !kTestQuestionListRef.current) return;

    const questionList = kTestQuestionListRef.current;
    const questionButton = document.getElementById(`aQuestion_${currentQuestionIndex}`);
    if (!questionButton) return;

    // Use the rendered positions (rather than offsets) so the number stays in
    // the centre even after the list has already been scrolled.
    const centreSelectedQuestion = () => {
      const listRect = questionList.getBoundingClientRect();
      const buttonRect = questionButton.getBoundingClientRect();
      const left = Math.max(
        0,
        questionList.scrollLeft +
          (buttonRect.left + buttonRect.width / 2) -
          (listRect.left + listRect.width / 2)
      );

      if (typeof questionList.scrollTo === "function") {
        questionList.scrollTo({ left, behavior: "smooth" });
      } else {
        questionList.scrollLeft = left;
      }
    };

    const frameId = requestAnimationFrame(centreSelectedQuestion);
    return () => cancelAnimationFrame(frameId);
  }, [currentQuestionIndex, hasQuestions, isKTestNavigation, questions.length]);

  return (
    <form id="form1">
      <div
        id="divBackground"
        className="divBackgroundT"
        onClick={() => toggleSolution(false)}
      ></div>
      <table width="100%" border={0} cellSpacing="0" cellPadding="0">
        <tbody>
          <tr id="trAnsHead" className="trAnsHeadT">
            <td id="tdNavigationHolder" className="tdNavigationHolderT">
              <table className="CssTable" border={0} cellPadding="2" cellSpacing="0">
                <tbody>
                  <tr id="trTop1">
                    <td align="left" className="tdTop1T">
                      <a 
                        id="linkbtnExit" 
                        className="tdTop2T" 
                        href="#"
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                          e.preventDefault();
                          
                          let calledFrom = searchParams.get("calledFrom");
                          
                          if (calledFrom) {
                            if (calledFrom.includes("StudyRoom")) {
                              const separator = calledFrom.includes("?") ? "&" : "?";
                              calledFrom = `${calledFrom}${separator}lastPage=AnswerSheet`;
                            }
                            window.location.href = calledFrom;
                          } else {
                            window.location.href = '/STUDENT/MSR/ExploreActivities';
                          }
                        }}
                      >
                        &nbsp;&nbsp;Done&nbsp;&nbsp;
                      </a>
                    </td>
                    <td id="tdASGuide" className="tdTop3T" style={{ width: "20%", paddingLeft: "10px", clip: "rect(auto auto auto auto)", backgroundColor: "#20699B", color: "white", fontSize: "10pt" }}>
                      <label id="lblLessonName">{loading || !currentQuestion ? "-" : currentQuestion.lessonName}</label>
                    </td>
                    <td id="tdQnInd" width="21%" style={{ backgroundColor: "#20699B", color: "white", fontFamily: "Arial", fontSize: "10pt" }} >
                      <label id="lblNoOfQns">
                        Question <span id="spanCurrentQuestionNo">{loading || !currentQuestion ? "-" : currentQuestion.questionIndex}</span> of &nbsp;
                        <span id="spanTotalNoOfQuestions">{loading ? "-" : totalQuestions}</span>
                      </label>
                    </td>
                    <td className="tdTop4T" align="left">
                      <div
                        id="divSolutionButton"
                        className="divSolutionButtonT"
                        style={{
                          opacity: loading || !currentQuestion?.hasSolution ? 0.5 : 1,
                          cursor: loading || !currentQuestion?.hasSolution ? 'default' : 'pointer'
                        }}
                        onClick={() => toggleSolution(true)}
                      > Solution
                      </div>
                    </td>
                    {isKTestNavigation ? (
                    <td id="tdKTestNavigation" align="right" valign="top" className="tdKTestNavigationT">
                        <div id="divKTestNavigation" className="divKTestNavigationT">
                            <button
                              id="btnKTestPreviousQuestion"
                              type="button"
                              className="btnKTestPreviousQuestionT"
                              aria-label="Previous"
                              disabled={loading || isFirstQuestion}
                              onClick={handlePrevious}
                            >
                              <img src={nav_previous_n} alt="Previous" />
                            </button>
                            <div
                              id="divKTestQuestionNoList"
                              ref={kTestQuestionListRef}
                              className="divKTestQuestionNoListT"
                            >
                              {questions.map((question, index) => (
                                <button
                                  key={question.questionIndex}
                                  id={`aQuestion_${index}`}
                                  type="button"
                                  aria-label={`Question ${question.questionIndex}`}
                                  aria-current={index === currentQuestionIndex ? "true" : undefined}
                                  className="btnKTestQuestionT"
                                  style={{
                                    color: question.marks === 0 ? "#ff0000" : "#00bb00",
                                    borderColor: index === currentQuestionIndex
                                      ? (question.marks === 0 ? "#ff0000" : "#00bb00")
                                      : "#20699B",
                                    opacity: index === currentQuestionIndex ? 1 : 0.5,
                                    cursor: index === currentQuestionIndex ? "default" : "pointer",
                                  }}
                                  disabled={loading || index === currentQuestionIndex}
                                  onClick={() => selectQuestion(index)}
                                >
                                  {question.questionIndex}
                                </button>
                              ))}
                            </div>
                            <button
                              id="btnKTestNextQuestion"
                              type="button"
                              className="btnKTestNextQuestionT"
                              aria-label="Next"
                              disabled={loading || isLastQuestion || !hasQuestions}
                              onClick={handleNext}
                            >
                              <img src={nav_next_n} alt="Next" />
                            </button>
                        </div>
                    </td>
                    ) : (
                    <td id="tdATestNavigation" align="right" valign="top" className="tdATestNavigationT">
                        <div id="divATestNavigation" >
                            <a
                              id="btnATestPreviousQuestion"
                              className="btnATestPreviousQuestionT"
                              style={{ cursor: loading || isFirstQuestion ? 'default' : 'pointer', opacity: loading || isFirstQuestion ? 0.5 : 1 }}
                              onClick={handlePrevious}
                            >
                              <img src={nav_previous_n} alt="Previous" />
                            </a>
                            <div className="divATestQuestionNoListT"></div>
                            <a
                              id="btnATestNextQuestion"
                              className="btnATestNextQuestionT"
                              style={{ cursor: loading || isLastQuestion || !hasQuestions ? 'default' : 'pointer', opacity: loading || isLastQuestion || !hasQuestions ? 0.5 : 1 }}
                              onClick={handleNext}
                            >
                              <img src={next_test} alt="Next" />
                            </a>
                        </div>
                    </td>
                    )}
                  </tr>
                  <tr id="trTop2">
                      <td className="tdTop5T">&nbsp;</td>
                      <td id="td2" className="tdTop6T" align="left" valign="middle">
                        {!loading && currentQuestion && statusInfo.icon && (
                          <img
                            id="imgQuestionStatus"
                            className="imgQuestionStatusT"
                            width="100%"
                            alt="Question Status"
                            src={statusInfo.icon}
                            style={{width:'35px', height:'32px'}}
                          />
                        )}
                        &nbsp;
                        <span id="spanAnswerComment" className="spanAnswerCommentT" style={{ fontSize: "10pt" }}>
                            {statusInfo.comment}
                        </span>
                      </td>
                      {/* <td id="tdMarks" className="tdMarksT">
                        Marks: {loading || !currentQuestion ? "-" : currentQuestion.marks}
                      </td> */}
                      <td colSpan={3} className="tdTop7T" valign="middle" align="center"></td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          <tr></tr>

          <tr className="trowT">
              <td id="tdQn" valign="top" className="tdQnT">
                  <div id="divFrame" className="divFrameT"></div>
                  {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                        <Loading />
                    </div>
                  ) : error ? (
                    <div style={{ color: 'red', padding: '20px' }}>Error: {error}</div>
                  ) : !currentQuestion ? (
                    <div style={{ padding: '20px' }}>No questions to display.</div>
                  ) : (
                    <iframe
                        ref={iframeRef}
                        id="iFrameQn"
                        className="iFrameQnT"
                        scrolling="yes"
                        title="Question Content Frame"
                        // Removed srcDoc to use doc.write + onLoad
                        onLoad={handleIframeLoad}
                        key={currentQuestion.questionIndex}
                        style={{ height:"90vh", width: "100%", border: "none" }}
                    ></iframe>
                  )}
              </td>
          </tr>
        </tbody>
      </table>

      {hasQuestions && (
          <div
            id="divSolutionIFrameT"
            className="CssPCSDiv"
          >
            <table className="CssTable CssTableClose">
              <tbody>
                <tr id="trSolutionIFrameTitle" className="CssTable">
                  <td className="CssTableT" id="CssTable">
                    <span id="spPCSTitle" className="cssSpTitle">
                      Step by step solution
                    </span>
                  </td>
                  <td className="CssTable" id="CssTable2T"
                  style={{display: "flex", justifyContent: "flex-end", alignItems: "center", padding: "10px 20px"}} 
                  >
                    <img
                      id="imgSolutionIFrameClose"
                      src={Close}
                      alt="Close"
                      className="cssImgCloseT"
                      onClick={() => toggleSolution(false)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="solution-scroll-area">
                    {solutionLoading ? (
                      <div className="answer-sheet-solution-loading">
                        <Loading />
                      </div>
                    ) : showSolution && solutionHtml ? (
                      <iframe
  id="iFrameSolution"
  title="Solution"
  srcDoc={solutionHtml || ""}
  scrolling="yes"
  style={{ width: "100%", display: "block", border: "0"}}
  onLoad={() => {
    const solDiv = document.getElementById("divSolutionIFrameT");

    const iframe = document.getElementById("iFrameSolution") as HTMLIFrameElement | null;
    if (!iframe) {
      console.error("[SOL] iframe element missing after onLoad");
      return;
    }

    // helper: try to access the iframe doc + compute height
    let tries = 0;
    const maxTries = 8;
    const tryCompute = () => {
      tries++;
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) {
          throw new Error("no iframe document yet");
        }

        // Expand legacy Ksd content before normalising the step-table layout.
        // This retains the cell that originally held the Show button.
        try {
          doc.querySelectorAll<HTMLImageElement>("img[data-target]").forEach((imgEl) => {
            const targetId = imgEl.getAttribute("data-target");
            if (!targetId) return;
            const targetDiv = doc.getElementById(targetId) as HTMLElement | null;
            if (!targetDiv) return;

            const content = targetDiv.dataset.ksdContent;
            const parentCell = imgEl.closest("td");
            if (content && parentCell) {
              parentCell.innerHTML = content;
              parentCell.dataset.answerSheetExpanded = "true";
              targetDiv.remove();
            } else {
              targetDiv.style.display = "block";
              imgEl.remove();
            }
          });
        } catch (e) {
          console.warn("[SOL] legacy content expansion error", e);
        }

        // Show and format the remaining solution step rows.
        try {
          doc.querySelectorAll("tr[id^='trSolStepLHS']").forEach((tr) => {
            (tr as HTMLElement).style.display = "table-row";
          });
          structureSolutionSteps(doc);
        } catch (e) { /* ignore */ }

        const solutionSteps = Array.from(
          doc.querySelectorAll<HTMLElement>("tr[id^='trSolStepLHS']")
        );
        const solutionPanel = document.getElementById("divSolutionIFrameT");
        const title = solutionPanel?.querySelector<HTMLElement>(".CssTableClose");
        const footer = document.getElementById("tblNavPCS");

        if (solutionSteps.length > 0 && solutionPanel && title && footer) {
          const maxPanelHeight = window.innerHeight * 0.5;
          const panelChromeHeight = title.offsetHeight + footer.offsetHeight;
          const totalStepsHeight = solutionSteps.reduce(
            (totalHeight, step) => totalHeight + step.getBoundingClientRect().height,
            0
          );
          const panelHeight = Math.min(
            maxPanelHeight,
            totalStepsHeight + panelChromeHeight
          );

          solutionPanel.style.setProperty("height", `${panelHeight}px`, "important");
          iframe.style.height = `${Math.max(0, panelHeight - panelChromeHeight)}px`;
        }

      } catch (err) {
        if (tries < maxTries) {
          // retry a few times (race condition protection)
          setTimeout(tryCompute, 120);
        } else {
          console.error("[SOL] could not read iframe content/document after retries:", err);
        }
      }
    };

    // start the compute cycle
    setTimeout(tryCompute, 0);
  }}
/>
        ) : null}
            </div>
            <table id="tblNavPCS" style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td id='tdPCSBottom2'>
                    <span id="spanPCSButtons">
                      <img
                        id="btnPCSClose"
                        src={got_it_btn_n}
                        alt="Got it"
                        onClick={() => toggleSolution(false)}
                        style={{
                          // width: "100%",
                          // height: "40px",
                          backgroundSize: "cover",
                          marginRight: "30px",
                          marginTop: "5px",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          cursor: "pointer",
                          // marginBottom: "5px",
                        }}
                      />
                    </span>
                  </td>
                </tr>
              </tbody>

            </table>
          </div>
      )}
    </form>
  );
};

export default AnswerSheet;
